<template>
  <div v-if="open">
    <Teleport to="body">
      <div
        v-if="open"
        :class="[
          'modalWrapper fixed top-0 left-0 z-7000! h-full w-screen justify-center overflow-hidden',
        ]"
        :style="{ zIndex }"
      >
        <transition name="wrapper" appear>
          <div
            v-if="open"
            :class="[
              'absolute top-0 left-0 flex h-screen w-screen items-center justify-center bg-neutral-950/70 p-0 md:p-6',
            ]"
          >
            <transition :name="'slide'" appear>
              <div
                v-if="open"
                :class="[
                  'bg-white-500 relative flex max-h-full max-w-screen overflow-hidden rounded-md',
                ]"
              >
                <div v-if="showCloseButton" class="absolute top-2 right-4">
                  <TButton
                    type="tonal"
                    color="neutral"
                    name="close"
                    icon="close"
                    @click="closeModal"
                  />
                </div>
                <div
                  class="overflow-y-auto p-4 md:p-10"
                  :class="{
                    'pt-12': showCloseButton,
                  }"
                >
                  <slot />
                </div>
              </div>
            </transition>
          </div>
        </transition>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import TButton from '../Button/TButton.vue';

const props = withDefaults(
  defineProps<{
    open: boolean;
    showCloseButton?: boolean;
  }>(),
  {
    showCloseButton: true,
  },
);

const emits = defineEmits(['close']);

function closeModal(): void {
  emits('close');
}

const closeEventCb = (e: KeyboardEvent): void => {
  if (e.code === 'Escape') {
    closeModal();
  }
};

onMounted(() => {
  document.documentElement.addEventListener('keydown', closeEventCb);
});
onBeforeUnmount(() => {
  document.documentElement.removeEventListener('keydown', closeEventCb);
});

const DEFAULT_Z_INDEX = 4000;
const zIndex = ref(DEFAULT_Z_INDEX);

watch(
  () => props.open,
  (open) => {
    if (open) {
      // traverse the dom for number of modals open
      const modals = document.querySelectorAll('.modalWrapper');
      const numModals = modals.length;
      // set the z-index of the modal to be 4000 + numModals
      zIndex.value = DEFAULT_Z_INDEX + numModals;
    }
  },
  { immediate: true },
);
</script>

<style scoped>
.wrapper-enter-active {
  will-change: opacity, transform;
  animation: fade-in 0.2s;
}

.wrapper-leave-active {
  will-change: opacity, transform;
  animation: fade-out 0.2s;
  animation-delay: 0.2s;
}

.slide-enter-active {
  will-change: opacity, transform;
  animation: wait-and-slide-up 0.2s ease-out;
}

.slide-leave-active {
  will-change: opacity, transform;
  animation: slide-down 0.2s ease-out;
}

.slide-leave-active {
  will-change: opacity, transform;
  animation: scale-down 0.2s ease-out;
}

@keyframes fade-in {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

@keyframes fade-out {
  0% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}

@keyframes slide-down {
  0% {
    transform: translateY(0);
    opacity: 1;
  }

  100% {
    transform: translateY(100%);
    opacity: 0;
  }
}

@keyframes wait-and-slide-up {
  0% {
    transform: translateY(50%);
    opacity: 0;
  }

  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
